import { sanitizeCmsHtml } from '@shared/helpers/sanitize-cms-html';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@app/store';
import { MixedObject } from '@core/interfaces/mixed-object';
import { Block } from '@core/models/block/block.model';
import { EditionDimensionsService } from '@shared/services/edition/edition-dimensions.service';
import { ListDetailsService } from '@shared/services/list/list-details.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: '[app-snippet-block]',
  templateUrl: './snippet-block.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SnippetBlockComponent implements OnInit, OnDestroy {
  innerContentHtml: SafeHtml = '';
  @Input() blockStyles: MixedObject = {};
  private destroy$ = new Subject<void>();
  private _snippetContent = '';

  constructor(private store: Store, private domSanitizer: DomSanitizer) {
  }

  _item: Block;

  get item(): Block {
    return this._item;
  }

  /**
   * Dozwolone nazwy znaczników opakowujących treść snippetu.
   *
   * AT-9147: `tagName` pochodzi z ustawień bloku (treść CMS) i był wklejany wprost
   * w `<${tagName}>`. Wartość w rodzaju `div onload=alert(1)` dopisywała atrybut
   * zdarzenia — nazwa znacznika nie ma prawa być kodem.
   */
  private static readonly ALLOWED_TAGS = [
    'div', 'span', 'p', 'section', 'article', 'aside', 'header', 'footer',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'small',
  ];

  @Input() set item(block: Block) {
    const tagName = SnippetBlockComponent.safeTagName(block.settings.tagName);
    this.innerContentHtml = this.domSanitizer.bypassSecurityTrustHtml(sanitizeCmsHtml(`<${tagName}>${block.content}</${tagName}>`));
    this._item = block;
  }

  private static safeTagName(tagName: unknown): string {
    const candidate = String(tagName ?? '').trim().toLowerCase();

    return SnippetBlockComponent.ALLOWED_TAGS.includes(candidate) ? candidate : 'div';
  }

  get isUiSnippet(): boolean {
    return (
      this.item?.content.startsWith('<<')
      && this.item?.content.endsWith('>>')
    );
  }

  get isCountSnippet(): boolean {
    return this.item?.content === '<<frontSnippet(items_count_on_list)>>';
  }

  /** Snippet „Wymiary" wybranej edycji — reaktywny, zasilany przez konfigurator ceny. */
  get isDimensionsSnippet(): boolean {
    return this.item?.content === '<<frontSnippet(edition_dimensions)>>';
  }

  get store$(): Observable<any> {
    const [obj] = this.splitSnippetFunction();
    return this.store.select(obj);
  }

  ngOnInit() {
    // Only log for count snippets to reduce console spam
    if (this.item?.content === '<<frontSnippet(items_count_on_list)>>') {
      // Initialize with current value
      this._snippetContent = ListDetailsService.objectCountOnList.value.toString();

      // Subscribe to the BehaviorSubject and re-subscribe when it changes
      this.subscribeToCountUpdates();
    } else if (this.isDimensionsSnippet) {
      // Wymiary wybranej edycji — start z bieżącej wartości i nasłuch zmian z konfiguratora.
      this._snippetContent = EditionDimensionsService.value.value;
      this.subscribeToDimensionUpdates();
    } else {
      // Silent initialization for other snippets
      if (this.item?.content.startsWith('<<') && this.item?.content.endsWith('>>')) {
        // This is a UI snippet but not the count one
        this._snippetContent = '';
      }
    }
  }

  private subscribeToCountUpdates() {
    // Unsubscribe from previous subscription
    this.destroy$.next();

    // Subscribe to current BehaviorSubject
    ListDetailsService.objectCountOnList
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        const oldValue = this._snippetContent;
        this._snippetContent = count.toString();
        if (oldValue !== this._snippetContent) {
          // Value changed
        }
      });

    // Subscribe to BehaviorSubject changes and re-subscribe when it changes
    ListDetailsService.behaviorSubjectChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.subscribeToCountUpdates();
      });
  }

  private subscribeToDimensionUpdates() {
    EditionDimensionsService.value
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this._snippetContent = value;
      });
  }

  ngOnDestroy() {
    // Only log for count snippets to reduce console spam
    if (this.item?.content === '<<frontSnippet(items_count_on_list)>>') {
      // Component destroyed
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get snippetContent(): string {
    if (!this.isUiSnippet) {
      return '';
    }

    if (this.item?.content === '<<frontSnippet(items_count_on_list)>>' || this.isDimensionsSnippet) {
      return this._snippetContent;
    }

    return '';
  }

  getLineClampClass() {
    const styles = this.blockStyles.basic || {};

    return {'lineClamp': !!styles['-webkitLineClamp']};
  }

  /** Czy w stylach bloku jest text_collapse_max_length – wtedy używamy zwijania (jak w innych blokach). */
  hasTextCollapse(): boolean {
    return this.getTextCollapseMaxLength() > 0;
  }

  getTextCollapseMaxLength(): number {
    if (this.blockStyles?.basic?.text_collapse_max_length != null) {
      return Number(this.blockStyles.basic.text_collapse_max_length);
    }
    if (this.blockStyles?.basic?.textCollapseMaxLength != null) {
      return Number(this.blockStyles.basic.textCollapseMaxLength);
    }
    if (this.item?.styles?.all?.basic?.text_collapse_max_length != null) {
      return Number(this.item.styles.all.basic.text_collapse_max_length);
    }
    if (this.item?.styles?.all?.basic?.textCollapseMaxLength != null) {
      return Number(this.item.styles.all.basic.textCollapseMaxLength);
    }
    if (this.item?.styles) {
      for (const key of Object.keys(this.item.styles)) {
        const v = this.item.styles[key]?.basic?.text_collapse_max_length ?? this.item.styles[key]?.basic?.textCollapseMaxLength;
        if (v != null) return Number(v);
      }
    }
    return 0;
  }

  private splitSnippetFunction(): any[] {
    const startIndex = this.item.content.indexOf('(');
    const endIndex = this.item.content.indexOf(')');
    const func = this.item.content.slice(startIndex + 1, endIndex);
    return func.split('.');
  }
}
